import { useState, useRef, type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SimpleTooltip.scss';

type Props = {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  maxWidth?: number;
};

type Placement = 'top' | 'bottom';

const SimpleTooltip = ({ content, children, className, maxWidth = 299 }: Props) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState<Placement>('bottom');

  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = () => {
    const wrapperEl = wrapperRef.current;
    const tooltipEl = tooltipRef.current;
    if (!wrapperEl || !tooltipEl) return;

    const rect = wrapperEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    const padding = 8;

    let top = rect.bottom + scrollY + 8;
    let left = rect.left + scrollX;

    const ttRect = tooltipEl.getBoundingClientRect();

    if (left + ttRect.width > viewportWidth - padding) {
      left = viewportWidth - padding - ttRect.width;
    }
    if (left < padding) {
      left = padding;
    }

    if (top + ttRect.height - scrollY > viewportHeight - padding) {
      top = rect.top + scrollY - 8;
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }

    setPos({ top, left });
  };

  const handleEnter = () => {
    setVisible(true);
  };

  const handleLeave = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (!visible) return;

    const frame = requestAnimationFrame(() => {
      updatePosition();
    });

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [visible]);

  return (
    <>
      <span
        ref={wrapperRef}
        className={`simple-tooltip ${className ?? ''}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {children}
      </span>

      {visible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className={
              `simple-tooltip__content simple-tooltip__content--fixed ` +
              (placement === 'top' ? 'simple-tooltip__content--top' : 'simple-tooltip__content--bottom')
            }
            style={{ top: pos.top, left: pos.left, maxWidth }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default SimpleTooltip;
